import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';

import { HttpService } from '@shared/services';
import { TenantStore } from '@signal-store/tenant.store';

type OfficeFileData = {
  fileId: number;
  fileName: string;
  ext: string;
  action: 'view' | 'edit';
  readOnly: boolean;
};

type OfficeDiscoveryData = {
  favIconUrl: string;
  ttl: number;
  wopiSrc: string;
};

@Injectable({ providedIn: 'root' })
export class OfficeService {
  private http = inject(HttpService);
  private tenantStore = inject(TenantStore);
  private router = inject(Router);

  discovery({ fileId, ...data }: OfficeFileData) {
    const params = this.http.buildHttpParams(data);
    return this.http.get<OfficeDiscoveryData>(`_api/wopi/Discovery/${fileId}`, {
      httpOptions: { params },
      useLoader: false,
    });
  }

  load(discoData: OfficeDiscoveryData, searchData: OfficeFileData) {
    document.title = searchData?.fileName || 'CIPO® Software';

    this.replaceFavIcon(discoData?.favIconUrl);

    const frame = document.createElement('iframe');
    frame.name = 'office_frame';
    frame.id = 'office_frame';
    frame.title = 'CIPO® Software - Office Frame';
    frame.setAttribute('allowfullscreen', 'true');
    frame.setAttribute(
      'sandbox',
      'allow-scripts allow-same-origin allow-forms allow-popups allow-top-navigation allow-popups-to-escape-sandbox',
    );

    document.body.appendChild(frame);
    (document.getElementById('office_form') as HTMLFormElement).submit();
  }

  open(file: any, readOnly: boolean, sameTab = false): boolean {
    const tenantIdentifier = this.tenantStore.selectedTenantName();
    if (!tenantIdentifier || !file?.name) {
      return false;
    }

    const data: OfficeFileData = {
      fileId: file.versionId,
      fileName: file.name,
      ext: file.name.substring(file.name.lastIndexOf('.') + 1),
      action: readOnly ? 'view' : 'edit',
      readOnly: readOnly,
    };

    const urlTree = this.router.createUrlTree(['/', tenantIdentifier, 'office'], {
      queryParams: { f: this.officeEncodeData(data) },
    });
    const fullUrl = this.router.serializeUrl(urlTree);
    if (sameTab) {
      window.location.href = fullUrl;
    } else {
      window.open(fullUrl, '_blank');
    }
    return true;
  }

  private replaceFavIcon(favicon: string) {
    if (!favicon) {
      return;
    }

    const links = document.querySelectorAll("link[rel~='icon']");
    links.forEach(link => {
      document.head.removeChild(link);
    });

    const link = document.createElement('link');
    link.rel = 'icon';
    link.href = favicon;
    document.head.appendChild(link);
  }

  encodeText(text: string) {
    const bytes = new TextEncoder().encode(text);
    const binString = String.fromCodePoint(...bytes);
    return btoa(binString);
  }

  decodeText(base64: string) {
    const binString = atob(base64);
    const bytes = Uint8Array.from(binString, m => m.codePointAt(0));
    return new TextDecoder().decode(bytes);
  }

  encodeData(data: OfficeFileData) {
    const text = JSON.stringify(data);
    return this.encodeText(text);
  }

  decodeData(base64: string): OfficeFileData {
    const text = this.decodeText(base64);
    return JSON.parse(text);
  }

  officeEncodeData(data: OfficeFileData) {
    const ed = this.encodeData(data).replaceAll('=', '');
    return this.encodeText(`eY5CUm${ed}oFBQ70`).replaceAll('=', '');
  }

  officeDecodeData(encoded: string) {
    const ed = this.decodeText(encoded).slice(6, -6);
    return this.decodeData(ed);
  }
}
